import React, { useEffect } from "react";
import { useContext, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Card,
  Text,
  Spacer,
  Grid,
  Toggle,
  Button,
  Divider,
} from "@geist-ui/core";
import { Plus, Trash2, AlertCircle } from "@geist-ui/icons";
import LoadingScreen from "../../../../components/loading-screen";
import { motion } from "framer-motion";


import { MultistepFormButtonsV2 } from "../../../../components/vertical-multistep-form/multistep-form-buttons";
import API_CLIENT from "../../../../utility/api-client";
import { AppContext } from "../../direct-router";
import { InvestmentContextFull } from "../investment-process-full";
import { INVESTOR_TYPE } from "../../../../types/onboarding_types";
import {
  AdditionalTaxCountry,
  BankAndTax,
  LEGAL_ENTITY_TYPE,
} from "../../../../types/inventure-user_types";
import { GeistHookFormInput } from "../components/geist-form-inputs";

type BankAndTaxFullProps = {
  back: () => void;
  next: () => void;
};

export default function BankAndTaxFull(props: BankAndTaxFullProps) {
  const investment_context = useContext(InvestmentContextFull);
  const app_context = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const user = app_context.user;
  const [tins, setTins] = useState<AdditionalTaxCountry[]>();

  var ownr =
    user?.natural_person?.forename + " " + user?.natural_person?.surname;
  if (user?.investor_type == INVESTOR_TYPE.LEGAL_PERSON && user?.legal_person) {
    var type = "GmbH";
    if (user.legal_person.legal_entity_type == LEGAL_ENTITY_TYPE.UG)
      type = "UG";
    ownr = user.legal_person.company_name + " " + type;
  }

  const {
    control,
    formState,
    getValues,
    formState: { errors, isValid },
    handleSubmit,
    watch,
  } = useForm<any>({ mode: "onChange" });

  const hide_crs = watch("tax_germany_only");
  const is_us_taxed = watch("no_us_taxation");

  function submit(data: BankAndTax) {
    data.tax_number = "";
    tins && (data.fatca_additional_tax_countries = JSON.stringify(tins));
    window.scrollTo(0, 0);
    investment_context.setLoading(true);

    API_CLIENT.putBankAndTax(
      app_context.token,
      data,
      (user) => {
        app_context.updateUser(user);
      },
      (error) => {
        console.log(error);
      }
    );
  }

  if (loading) {
    return <LoadingScreen className="w-full h-screen" />;
  }

  // Common card style for consistency
  const sectionCardStyle = {
      borderRadius: '16px',
      border: '1px solid #EAEAEA',
      boxShadow: 'none',
      padding: 'clamp(16px, 3vw, 24px)',
      marginBottom: '1.5rem',
      backgroundColor: '#FFFFFF',
      width: '100%'
  };

  const sectionTitleStyle = {
      fontFamily: '"Nunito Sans", sans-serif',
      fontSize: 'clamp(16px, 4vw, 18px)',
      fontWeight: 700,
      marginTop: 0,
      marginBottom: '1.5rem'
  };

  return (
    <div className="flex flex-col items-start justify-start min-h-screen w-full max-w-4xl">
      <form onSubmit={handleSubmit(submit)} className="w-full">
        <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-4 mb-2">
          <Text h3 style={{ margin: 0, fontSize: 'clamp(20px, 5vw, 24px)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '-0.02em' }}>
            Bank- und Steuerdaten
          </Text>
          <Text p type="secondary" style={{ margin: 0, fontSize: 'clamp(14px, 4vw, 16px)', lineHeight: 1.5 }}>
            Bitte geben Sie Ihre Bankverbindung für Auszahlungen sowie Ihre steuerlichen Informationen an.
          </Text>
        </div>

          {/* BANK ACCOUNT CARD */}
          <Card width="100%" style={sectionCardStyle}>
            <Text h4 style={sectionTitleStyle}>
              Auszahlungskonto
            </Text>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                 <Text small type="secondary" style={{ textTransform: 'uppercase', fontSize: '11px', fontWeight: 600 }}>Kontoinhaber</Text>
                 <Text p b style={{ margin: 0 }}>{ownr}</Text>
              </div>
              
              <Grid.Container gap={2}>
                 <Grid xs={24} md={12}>
                    <div className="w-full">
                        <GeistHookFormInput
                            name="bank"
                            control={control}
                            defaultValue={app_context.user?.bank_account?.bank}
                            required
                            label="Bank"
                            placeholder="z.B. Deutsche Bank"
                            error={errors.bank != null}
                        />
                    </div>
                 </Grid>
                 <Grid xs={24} md={12}>
                    <div className="w-full">
                        <GeistHookFormInput
                            name="bic"
                            control={control}
                            defaultValue={app_context.user?.bank_account?.bic}
                            required
                            label="BIC"
                            placeholder="BIC"
                            error={errors.bic != null}
                        />
                    </div>
                 </Grid>
                 <Grid xs={24}>
                     <div className="w-full">
                         <GeistHookFormInput
                            name="iban"
                            control={control}
                            defaultValue={app_context.user?.bank_account?.iban}
                            required
                            label="IBAN"
                            placeholder="IBAN"
                            error={errors.iban != null}
                        />
                    </div>
                 </Grid>
              </Grid.Container>
            </div>
          </Card>

          {/* TAX DATA CARD */}
          <Card width="100%" style={sectionCardStyle}>
            <Text h4 style={sectionTitleStyle}>
               Steuerdaten
            </Text>
             <div className="flex flex-col gap-4">
                <Grid.Container gap={2}>
                    <Grid xs={24}>
                        <div className="w-full">
                            <GeistHookFormInput
                                name="tax_office"
                                control={control}
                                defaultValue={app_context.user?.taxInformation?.tax_office}
                                required
                                label="Wohnsitzfinanzamt"
                                placeholder="z.B. Finanzamt Charlottenburg"
                                error={errors.tax_office != null}
                            />
                        </div>
                    </Grid>
                    <Grid xs={24}>
                        <div className="w-full">
                            <GeistHookFormInput
                                name="tin"
                                control={control}
                                defaultValue={app_context.user?.taxInformation?.tax_identification_number}
                                required
                                label="Steueridentifikationsnummer"
                                placeholder="z.B. 01 234 567 890"
                                error={errors.tin != null}
                            />
                        </div>
                    </Grid>
                </Grid.Container>
             </div>
          </Card>

          {/* TAX STATUS CARD */}
          <Card width="100%" style={sectionCardStyle}>
            <Text h4 style={sectionTitleStyle}>
               Steuerliche Informationen
            </Text>
             <div className="flex flex-col gap-6">
                
                {/* US Tax */}
                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-2xl">
                    <div className="w-4/5 pr-4">
                        <Text p b style={{ margin: 0, fontSize: "14px" }}>US-Steuerpflicht</Text>
                        <Text p style={{ margin: 0, fontSize: "13px", color: "#666" }}>
                            Ich bin US-Staatsbürger oder in sonstiger Weise steuerlich in den USA ansässig.
                        </Text>
                    </div>
                     <Controller
                        name="no_us_taxation"
                        control={control}
                        defaultValue={is_us_taxed}
                        render={({ field }) => (
                             <Toggle
                                checked={field.value}
                                onChange={(e) => field.onChange(e.target.checked)}
                                scale={1.2}
                             />
                        )}
                    />
                </div>

                 {is_us_taxed && (
                    <div className="flex items-start gap-3 bg-red-50 text-red-600 p-4 rounded-xl border border-red-100">
                      <AlertCircle size={20} className="mt-0.5" />
                      <Text p style={{ margin: 0, fontSize: "14px" }}>
                        Es dürfen keine US-Staatsbürger oder anderweitig in den USA
                        steuerpflichtige Bürger investieren. Bitte kontaktieren Sie unseren Kundenservice.
                      </Text>
                    </div>
                  )}

                 <Divider />

                 {/* Germany Tax Only */}
                 <div className="flex items-center justify-between p-4 border border-gray-200 rounded-2xl">
                    <div className="w-4/5 pr-4">
                        <Text p b style={{ margin: 0, fontSize: "14px" }}>Steueransässigkeit Deutschland</Text>
                        <Text p style={{ margin: 0, fontSize: "13px", color: "#666" }}>
                             Ich bin ausschließlich in Deutschland steuerlich ansässig.
                        </Text>
                    </div>
                     <Controller
                        name="tax_germany_only"
                        control={control}
                        defaultValue={user?.taxInformation?.tax_germany_only ?? true}
                        render={({ field }) => (
                             <Toggle
                                checked={field.value}
                                onChange={(e) => field.onChange(e.target.checked)}
                                scale={1.2}
                             />
                        )}
                    />
                </div>

                {!hide_crs && (
                    <div className="mt-4">
                       <ForeignTins onTinChange={(tins) => setTins(tins)} />
                    </div>
                )}

             </div>
          </Card>

          <Spacer h={2} />
          
          <MultistepFormButtonsV2
            backFunction={() => investment_context.prevStep()}
            back={true}
            disabled={!formState.isValid || is_us_taxed == true}
          />
        </div>
      </form>
    </div>
  );
}

interface ForeignTinsProps {
  onTinChange?: (tins: AdditionalTaxCountry[]) => void;
}

// Replicating styles locally since ForeignTins doesn't use Hook Form
const inputBaseStyle = {
    backgroundColor: '#FFFFFF',
    borderRadius: '16px',
    border: '1px solid #D4D4D4', 
    height: '56px',
    fontSize: '16px',
    color: '#000',
    width: '100%',
    padding: '0 20px',
    transition: 'all 0.4s cubic-bezier(0.25, 1, 0.5, 1)',
    boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
    outline: 'none',
    fontFamily: 'inherit'
};

const inputFocusStyle = {
    borderColor: '#000',
    borderWidth: '1px',
    backgroundColor: '#fff',
    boxShadow: '0 8px 20px rgba(0,0,0,0.08)',
    transform: 'scale(1.005) translateY(-2px)',
};

export function ForeignTins({ onTinChange }: ForeignTinsProps) {
  const app_context = useContext(AppContext);
  var tins_string =
    app_context.user?.taxInformation?.fatca_additional_tax_countries;
  var tins: AdditionalTaxCountry[] = tins_string && JSON.parse(tins_string);
  const [foreign_tins, setForeignTins] = useState<AdditionalTaxCountry[]>(
    tins ? tins : []
  );
  const [new_tin, setNewTin] = useState("");
  const [new_tin_country, setNewTinCountry] = useState("");

  const addTin = () => {
    if (new_tin == "" || new_tin_country == "") return;
    var new_tax_country: AdditionalTaxCountry = {
      tin: new_tin,
      country: new_tin_country,
    };
    setForeignTins([...foreign_tins, new_tax_country]);
    setNewTin("");
    setNewTinCountry("");
  };

  const deleteTin = (index: number) => {
    const new_tins = foreign_tins.filter((item, i) => i !== index);
    setForeignTins(new_tins);
  };

  useEffect(() => {
    onTinChange && onTinChange(foreign_tins);
  }, [foreign_tins]);

  // Helper for manual inputs style
  const getInputProps = (placeholder: string, value: string, onChange: (v: string) => void) => ({
      className: "w-full",
      style: inputBaseStyle,
      placeholder: placeholder,
      value: value,
      onChange: (e: any) => onChange(e.target.value),
      onFocus: (e: any) => {
         Object.assign(e.currentTarget.style, inputFocusStyle);
         const label = e.currentTarget.parentElement?.querySelector('label');
         if(label) label.style.color = '#000';
      },
      onBlur: (e: any) => {
         e.currentTarget.style.borderColor = '#D4D4D4';
         e.currentTarget.style.transform = 'scale(1) translateY(0)';
         e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.02)';
         const label = e.currentTarget.parentElement?.querySelector('label');
         if(label) label.style.color = '#444';
      }
  });

  return (
    <div className="flex flex-col gap-4 p-5 bg-gray-50 rounded-2xl border border-gray-100">
      <Text h5 style={{ margin: 0 }}>Weitere Steueransässigkeiten</Text>
      <Text p style={{ fontSize: "13px", color: "#666", margin: 0 }}>
        Bitte listen Sie sämtliche weiteren Staaten auf, in denen Sie steuerlich ansässig sind.
      </Text>
      
      {/* List of added TINs */}
      <div className="flex flex-col gap-2">
         {foreign_tins.length === 0 && (
             <div className="text-center py-4 text-gray-400 text-sm italic">
                Noch keine weiteren Länder hinzugefügt.
             </div>
         )}
         {foreign_tins.map((tin, i) => (
             <div key={i} className="flex items-center justify-between bg-white p-3 rounded-xl border border-gray-200 shadow-sm">
                 <div className="flex flex-col">
                     <Text b style={{ fontSize: "14px", margin: 0 }}>{tin.country}</Text>
                     <Text span style={{ fontSize: "12px", color: "#666" }}>TIN: {tin.tin}</Text>
                 </div>
                 <Button 
                    auto 
                    scale={0.6} 
                    icon={<Trash2 size={14} />} 
                    type="error" 
                    ghost 
                    style={{ borderRadius: '999px' }}
                    onClick={() => deleteTin(i)}
                 />
             </div>
         ))}
      </div>

      <Divider />

      {/* Add New TIN */}
      <div className="flex flex-col gap-3">
          <div style={{ marginBottom: '0.5rem' }}>
             <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: '#444',
                fontSize: '0.75rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                fontFamily: '"Nunito Sans", sans-serif',
                marginLeft: '4px',
                transition: 'color 0.2s ease'
             }}>Land</label>
             <input {...getInputProps("Land", new_tin_country, setNewTinCountry)} />
          </div>

          <div style={{ marginBottom: '0.5rem' }}>
             <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: '#444',
                fontSize: '0.75rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                fontFamily: '"Nunito Sans", sans-serif',
                marginLeft: '4px',
                transition: 'color 0.2s ease'
             }}>Steuer-ID</label>
             <input {...getInputProps("Steuer-ID / TIN", new_tin, setNewTin)} />
          </div>

          <motion.button 
             whileHover={!(new_tin == "" || new_tin_country == "") ? { scale: 1.02 } : {}}
             whileTap={!(new_tin == "" || new_tin_country == "") ? { scale: 0.98 } : {}}
             className={`w-full py-4 rounded-full font-bold text-sm uppercase tracking-tight transition-all flex items-center justify-center gap-2 ${
                (new_tin == "" || new_tin_country == "") 
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                : 'bg-black text-white shadow-lg cursor-pointer'
             }`}
             onClick={addTin}
             disabled={new_tin == "" || new_tin_country == ""}
          >
             <Plus size={16} />
             Hinzufügen
          </motion.button>
      </div>
    </div>
  );
}
